import type { SncrNotificacaoTipo } from "../types";

export type ReceituarioModelo =
  | "NRA"
  | "NRB"
  | "NRB2"
  | "NRR"
  | "NRT"
  | "RCE"
  | "ANTIMICROBIANO"
  | "SIMPLES";

export const RECEITUARIO_TIPO_SELECT_AUTO = "AUTO" as const;

export type ReceituarioTipoSelectValue =
  | typeof RECEITUARIO_TIPO_SELECT_AUTO
  | ReceituarioModelo;

export const RECEITUARIO_TIPO_SELECT_OPTIONS: Array<{
  value: ReceituarioTipoSelectValue;
  display: string;
}> = [
    { value: "AUTO", display: "Automático" },
    { value: "NRA", display: "NRA — Receita A (Amarela)" },
    { value: "NRB", display: "NRB — Receita B (Azul)" },
    { value: "NRB2", display: "NRB2 — Receita B2 (Azul)" },
    { value: "NRR", display: "NRR — Retinoides" },
    { value: "NRT", display: "NRT — Talidomida" },
    { value: "RCE", display: "RCE — Controle Especial" },
    { value: "ANTIMICROBIANO", display: "RET — Sujeita à Retenção" },
    { value: "SIMPLES", display: "Receita simples" },
  ];

export type ReceituarioProfile = {
  modelo: ReceituarioModelo;
  sncrTipo?: SncrNotificacaoTipo;
  consomeSncr: boolean;
  pdfTitle: string;
  codeValue: "REC_ESP" | "REC_SIM";
  label: string;
};

type ReceituarioItemInput = {
  tag?: string;
  name?: string;
  label?: string;
  class?: string;
  classe?: string;
  inputValue?: { class?: string };
  receituarioModelo?: ReceituarioModelo;
  sncrTipo?: SncrNotificacaoTipo;
  receituarioTipoManual?: boolean;
};

function upper(value?: string): string {
  return value?.toUpperCase?.() || "";
}

function classeMedicamento(item: ReceituarioItemInput): string {
  const raw =
    item?.class || item?.classe || item?.inputValue?.class || "";
  if (!raw || raw === "-") return "";
  return upper(raw);
}

function profileFromClasseTerapeutica(
  classe: string,
): ReceituarioProfile | null {
  if (!classe) return null;

  if (classe.includes("TALIDOM")) return profileNRT();
  if (classe.includes("RETINOID")) return profileNRR();

  if (classe.includes("ANOREX") || classe.includes("LISTA B2")) {
    return profileNRB2();
  }

  if (
    classe.includes("ENTORPEC") ||
    classe.includes("LISTA A") ||
    classe.includes("OPIÁCE") ||
    classe.includes("OPIACE") ||
    classe.includes("OPIOID") ||
    classe.includes("MORFIN") ||
    classe.includes("FENTANIL") ||
    classe.includes("FENTANYL")
  ) {
    return profileNRA();
  }

  if (
    classe.includes("BENZODIAZE") ||
    classe.includes("ANSIOLIT") ||
    classe.includes("HIPNÓT") ||
    classe.includes("HIPNOT") ||
    classe.includes("LISTA B1") ||
    classe.includes("LISTA B") ||
    classe.includes("PSICOTR")
  ) {
    return profileNRB();
  }

  if (
    classe.includes("ANTIMICRO") ||
    classe.includes("ANTIBIÓT") ||
    classe.includes("ANTIBIOT")
  ) {
    return profileAntimicrobiano();
  }

  if (
    classe.includes("CONTROLE ESPECIAL") ||
    classe.includes("LISTA C") ||
    classe.includes("ANTICONVULS") ||
    classe.includes("ANTIDEPRESS")
  ) {
    return profileRCE();
  }

  return null;
}

export function resolveReceituarioProfile(
  item: ReceituarioItemInput,
): ReceituarioProfile {
  if (item?.receituarioTipoManual && item?.receituarioModelo) {
    const manual = profileFromModelo(item.receituarioModelo, item.sncrTipo);
    if (manual) return manual;
  }

  const tag = upper(item?.tag);
  const classe = classeMedicamento(item);

  const fromClass = profileFromClasseTerapeutica(classe);
  if (fromClass) return fromClass;

  if (item?.receituarioModelo && item.receituarioModelo !== "SIMPLES") {
    const stamped = profileFromModelo(
      item.receituarioModelo,
      item.sncrTipo,
    );
    if (stamped) return stamped;
  }

  if (tag.includes("TALIDOMIDA") || tag.includes("NRT")) return profileNRT();
  if (tag.includes("RETINOID") || tag.includes("NRR")) return profileNRR();
  if (tag.includes("NRB2")) return profileNRB2();

  if (
    tag.includes("AMARELA") ||
    tag.includes("NRA") ||
    tag === "AM" ||
    tag.includes("RECEITA A") ||
    tag.includes("PRETA")
  ) {
    return profileNRA();
  }

  if (
    tag.includes("AZUL") ||
    tag.includes("NRB") ||
    tag === "AZ" ||
    tag.includes("RECEITA B")
  ) {
    return profileNRB();
  }

  if (
    tag.includes("ANTIMICRO") ||
    tag.includes("RETENÇÃO") ||
    tag.includes("RETENCAO")
  ) {
    return profileAntimicrobiano();
  }

  if (
    tag.includes("VERMELHA") ||
    tag.includes("CONTROLE ESPECIAL") ||
    tag.includes("ESPECIAL") ||
    tag.includes("RCE")
  ) {
    return profileRCE();
  }

  return profileSimples();
}

function profileNRA(): ReceituarioProfile {
  return {
    modelo: "NRA",
    sncrTipo: "NRA",
    consomeSncr: true,
    pdfTitle: "NOTIFICAÇÃO DE RECEITA A (AMARELA)",
    codeValue: "REC_ESP",
    label: "Receita A (Amarela)",
  };
}

function profileNRB(): ReceituarioProfile {
  return {
    modelo: "NRB",
    sncrTipo: "NRB",
    consomeSncr: true,
    pdfTitle: "NOTIFICAÇÃO DE RECEITA B (AZUL)",
    codeValue: "REC_ESP",
    label: "Receita B (Azul)",
  };
}

function profileNRB2(): ReceituarioProfile {
  return {
    modelo: "NRB2",
    sncrTipo: "NRB2",
    consomeSncr: true,
    pdfTitle: "NOTIFICAÇÃO DE RECEITA B2 (AZUL)",
    codeValue: "REC_ESP",
    label: "Receita B2 (Azul)",
  };
}

function profileNRR(): ReceituarioProfile {
  return {
    modelo: "NRR",
    sncrTipo: "NRR",
    consomeSncr: true,
    pdfTitle: "NOTIFICAÇÃO DE RECEITA — RETINOIDES",
    codeValue: "REC_ESP",
    label: "Receita de Retinoides",
  };
}

function profileNRT(): ReceituarioProfile {
  return {
    modelo: "NRT",
    sncrTipo: "NRT",
    consomeSncr: true,
    pdfTitle: "NOTIFICAÇÃO DE RECEITA — TALIDOMIDA",
    codeValue: "REC_ESP",
    label: "Receita de Talidomida",
  };
}

function profileRCE(): ReceituarioProfile {
  return {
    modelo: "RCE",
    sncrTipo: "RCE",
    consomeSncr: true,
    pdfTitle: "RECEITUÁRIO DE CONTROLE ESPECIAL",
    codeValue: "REC_ESP",
    label: "Receita de Controle Especial (Branca)",
  };
}

function profileAntimicrobiano(): ReceituarioProfile {
  return {
    modelo: "ANTIMICROBIANO",
    sncrTipo: "RET",
    consomeSncr: true,
    pdfTitle: "RECEITUÁRIO — ANTIMICROBIANO (RETENÇÃO)",
    codeValue: "REC_SIM",
    label: "Receita sujeita à retenção",
  };
}

function profileSimples(): ReceituarioProfile {
  return {
    modelo: "SIMPLES",
    consomeSncr: false,
    pdfTitle: "RECEITUÁRIO",
    codeValue: "REC_SIM",
    label: "Receita simples",
  };
}

function profileFromModelo(
  modelo: ReceituarioModelo,
  sncrTipo?: SncrNotificacaoTipo,
): ReceituarioProfile | null {
  switch (modelo) {
    case "NRA":
      return profileNRA();
    case "NRB":
      return profileNRB();
    case "NRB2":
      return profileNRB2();
    case "NRR":
      return profileNRR();
    case "NRT":
      return profileNRT();
    case "RCE":
      return profileRCE();
    case "ANTIMICROBIANO":
      return profileAntimicrobiano();
    case "SIMPLES":
      return profileSimples();
    default:
      return sncrTipo
        ? {
          ...profileSimples(),
          sncrTipo,
          consomeSncr: true,
          modelo: sncrTipo as ReceituarioModelo,
        }
        : null;
  }
}

export function needsSpecialReceituarioPdf(
  item: ReceituarioItemInput,
): boolean {
  const modelo = resolveReceituarioProfile(item).modelo;
  return (
    modelo === "NRA" ||
    modelo === "NRB" ||
    modelo === "NRB2" ||
    modelo === "NRR" ||
    modelo === "NRT" ||
    modelo === "RCE" ||
    modelo === "ANTIMICROBIANO"
  );
}

export function needsSncrNumero(item: ReceituarioItemInput): boolean {
  return resolveReceituarioProfile(item).consomeSncr;
}

export function stampReceituarioProfile<T extends Record<string, any>>(
  item: T,
): T & {
  class?: string;
  receituarioModelo: ReceituarioModelo;
  sncrTipo?: SncrNotificacaoTipo;
  pdfTitle: string;
  receituarioLabel: string;
  receituarioTipoManual?: boolean;
} {
  const classe =
    item?.class || item?.classe || item?.inputValue?.class || undefined;
  const withClass = {
    ...item,
    ...(classe && classe !== "-" ? { class: classe } : {}),
  };

  if (withClass.receituarioTipoManual && withClass.receituarioModelo) {
    const profile =
      profileFromModelo(withClass.receituarioModelo, withClass.sncrTipo) ||
      resolveReceituarioProfile(withClass);
    return {
      ...withClass,
      receituarioTipoManual: true,
      receituarioModelo: profile.modelo,
      sncrTipo: profile.sncrTipo,
      pdfTitle: profile.pdfTitle,
      receituarioLabel: profile.label,
    };
  }

  const profile = resolveReceituarioProfile({
    ...withClass,
    receituarioTipoManual: false,
  });
  return {
    ...withClass,
    receituarioTipoManual: false,
    receituarioModelo: profile.modelo,
    sncrTipo: profile.sncrTipo,
    pdfTitle: profile.pdfTitle,
    receituarioLabel: profile.label,
  };
}
